import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Link as LinkIcon, Gamepad2, Lock, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { copyText, openExternal, composeInvite } from "../lib/clipboard";

function FloatingParticles({ density = 12, color = "#FF2E9A" }) {
  const particles = Array.from({ length: density }, (_, i) => ({
    left: (i * 11 + 5) % 100,
    size: 1.5 + (i % 4) * 0.7,
    delay: (i * 0.6) % 8,
    duration: 9 + (i % 5),
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {particles.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute", top: 0,
            left: `${p.left}%`,
            width: p.size, height: p.size,
            borderRadius: "50%",
            background: color, boxShadow: `0 0 ${p.size * 3}px ${color}`,
            opacity: 0.45,
            animation: `vcFloatUp ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes vcFloatUp {
          0% { transform: translateY(110vh); opacity: 0; }
          15% { opacity: 0.7; }
          85% { opacity: 0.7; }
          100% { transform: translateY(-12vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

function WhatsAppIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
      <path
        fill="#25D366"
        d="M16 .5C7.44.5.5 7.44.5 16c0 2.8.74 5.43 2.04 7.71L0 32l8.5-2.46A15.49 15.49 0 0 0 16 31.5c8.56 0 15.5-6.94 15.5-15.5S24.56.5 16 .5Z"
      />
      <path
        fill="#FFFFFF"
        d="M16 4.5C9.65 4.5 4.5 9.65 4.5 16c0 2.43.76 4.69 2.05 6.55l-1.34 4.9 5.02-1.32A11.45 11.45 0 0 0 16 27.5c6.35 0 11.5-5.15 11.5-11.5S22.35 4.5 16 4.5Zm6.69 16.21c-.28.79-1.62 1.5-2.27 1.6-.6.08-1.37.12-2.21-.14-.51-.16-1.17-.39-2.01-.74-3.54-1.53-5.85-5.1-6.03-5.34-.18-.24-1.45-1.93-1.45-3.68 0-1.75.92-2.6 1.24-2.96.32-.36.7-.45.93-.45h.66c.21 0 .5-.08.78.6.28.69.96 2.39 1.05 2.56.09.18.14.39.03.62-.11.24-.16.39-.32.6-.16.21-.34.46-.49.62-.16.16-.33.34-.14.66.18.32.81 1.34 1.74 2.18 1.2 1.07 2.21 1.4 2.53 1.56.32.16.51.13.7-.08.18-.2.81-.94.99-1.27.18-.32.37-.27.62-.16.25.11 1.59.75 1.87.89.28.13.46.2.53.31.07.11.07.66-.21 1.45Z"
      />
    </svg>
  );
}

// Tiny floating hearts/sparkles around envelope — positioned absolutely so the top doesn't feel static
function EnvelopeOrbiters() {
  const items = [
    { Cmp: Heart,     left: "8%",  top: "12%", size: 14, color: "#FF2E9A", delay: 0,    dur: 3.6, fill: true },
    { Cmp: Sparkles,  left: "82%", top: "6%",  size: 16, color: "#FFB6E0", delay: 0.4,  dur: 4.2 },
    { Cmp: Heart,     left: "90%", top: "62%", size: 10, color: "#FF59B8", delay: 0.9,  dur: 3.2, fill: true },
    { Cmp: Sparkles,  left: "4%",  top: "70%", size: 12, color: "#C9B7FF", delay: 1.2,  dur: 4.6 },
    { Cmp: Heart,     left: "72%", top: "84%", size: 11, color: "#FF8FC8", delay: 1.6,  dur: 3.8, fill: true },
    { Cmp: Sparkles,  left: "20%", top: "32%", size: 10, color: "#FFD0EC", delay: 2.1,  dur: 5.2 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {items.map((it, i) => {
        const I = it.Cmp;
        return (
          <motion.div
            key={i}
            className="absolute"
            style={{ left: it.left, top: it.top }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: [0, 0.85, 0.85, 0],
              y: [-2, -16, -2],
              scale: [0.7, 1.05, 0.7],
              rotate: [-8, 8, -8],
            }}
            transition={{
              duration: it.dur,
              delay: it.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <I
              className="block"
              size={it.size}
              style={{
                color: it.color,
                fill: it.fill ? it.color : "none",
                filter: `drop-shadow(0 0 6px ${it.color})`,
              }}
              strokeWidth={2}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

/**
 * Step 4 of the Remotely flow — invite person to join.
 *  - Envelope hero (with floating hearts/sparkles)
 *  - Compact glowing ROOM CODE pill + privacy status line
 *  - Big magenta "Copy Invite Link"
 *  - Outline "Share on WhatsApp"
 *  - Small utility "Invite via QR" chip
 *  - Inline "I have a room code"
 *  - Continue-to-Lobby CTA reveals AFTER a share action (fade-up)
 */
export default function PlayRemotelyInvite({ room, onBack, onContinue, onJoinInstead }) {
  const [hasShared, setHasShared] = useState(false);
  const joinUrl = `${window.location.origin}/join/${room.code}`;
  const inviteText = composeInvite(room.host_name, joinUrl);

  const markShared = () => {
    if (!hasShared) {
      setHasShared(true);
      // Flash a confirmation chip in the waiting room (replaces the toast,
      // since the waiting room shows its own "Invite sent" pill at the top).
      sessionStorage.setItem("vc_invite_just_sent", "1");
      // Auto-advance to waiting room
      setTimeout(() => {
        onContinue?.();
      }, 1200);
    }
  };

  const copyLink = async () => {
    const ok = await copyText(joinUrl);
    if (ok) {
      toast.success("Link copied — paste it anywhere 💌");
      markShared();
    } else {
      toast.error("Couldn't copy");
    }
  };

  const copyCode = async () => {
    const ok = await copyText(room.code);
    if (ok) toast.success(`Room code ${room.code} copied`);
  };

  const shareWhatsApp = async () => {
    const url = `https://wa.me/?text=${encodeURIComponent(inviteText)}`;
    await openExternal(url);
    markShared();
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="remotely-invite-screen">
      <FloatingParticles density={14} color="#FF2E9A" />
      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-2 relative z-[2]">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="remotely-invite-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10" aria-hidden />
        </div>

        {/* Title */}
        <div className="text-left mb-1.5 relative z-[2]">
          <h1
            className="font-extrabold leading-[1.0] tracking-tight inline-flex items-center gap-2"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(1.85rem, 7.6vw, 2.2rem)",
              letterSpacing: "-0.025em",
              background: "linear-gradient(90deg, #FFD8E8 0%, #FF4FBF 55%, #FF0F7B 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "0 0 1px rgba(255, 46, 154, 0.6)",
              filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
            }}
            data-testid="remotely-invite-title"
          >
            Invite your person
            <motion.span
              animate={{ scale: [1, 1.18, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              style={{ filter: "drop-shadow(0 0 8px #FF2E9A)" }}
            >
              💌
            </motion.span>
          </h1>
          <p className="text-white/70 text-[13.5px] font-medium mt-1.5 leading-snug">
            Send the link. The chaos starts when they join
            <motion.span
              animate={{ scale: [1, 1.18, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block ml-1"
            >
              👀
            </motion.span>
          </p>
        </div>

        {/* Cinematic envelope hero — with floating hearts/sparkles */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex items-center justify-center my-2 mx-auto"
          style={{ width: 300, height: 230 }}
        >
          <EnvelopeOrbiters />
          <motion.img
            animate={{
              y: [0, -10, 0, -6, 0],
              rotate: [-3, 3, -2, 2, -3],
              scale: [1, 1.05, 1, 1.03, 1],
            }}
            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
            src="/assets/home/remotely-invite-envelope.png"
            alt=""
            className="w-[240px] object-contain relative z-[1]"
            style={{
              filter: "drop-shadow(0 0 28px rgba(255, 46, 154, 0.65)) drop-shadow(0 0 60px rgba(255, 46, 154, 0.35))",
            }}
          />
          {/* Soft heartbeat halo behind */}
          <motion.div
            aria-hidden
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 220,
              height: 220,
              background: "radial-gradient(circle, rgba(255,46,154,0.35) 0%, rgba(255,46,154,0.10) 45%, transparent 70%)",
              filter: "blur(8px)",
              zIndex: 0,
            }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.55, 0.85, 0.55] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        {/* ROOM CODE — compact glowing pill */}
        <div className="text-center mb-4">
          <div
            className="text-[10px] font-black tracking-[0.32em] mb-1.5"
            style={{ color: "rgba(255, 255, 255, 0.78)" }}
          >
            ROOM CODE
          </div>
          <button
            onClick={copyCode}
            data-testid="remotely-invite-code-pill"
            className="inline-flex items-center px-5 py-2 rounded-full font-extrabold text-white transition active:scale-[0.98]"
            style={{
              background: "rgba(15, 8, 22, 0.7)",
              border: "1.5px solid #FF2E9A",
              boxShadow: "0 0 12px rgba(255, 46, 154, 0.45)",
              fontFamily: "'Manrope', sans-serif",
              fontSize: "17px",
              letterSpacing: "0.16em",
              fontVariantNumeric: "tabular-nums",
            }}
            aria-label={`Copy room code ${room.code}`}
          >
            {room.code}
          </button>
          <div className="text-white/55 text-[11px] font-medium mt-2 flex items-center justify-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-emerald-400" style={{ boxShadow: "0 0 6px #34D399" }} aria-hidden />
            Private room · expires in 24h 🔒
          </div>
        </div>

        {/* PRIMARY: Copy Invite Link */}
        <div className="space-y-3 mb-3">
          <button
            onClick={copyLink}
            data-testid="remotely-invite-copy-link"
            className="vc-cta vc-cta-primary w-full rounded-full py-[16px] font-extrabold flex items-center justify-center gap-3"
            style={{ fontSize: "16.5px" }}
          >
            <LinkIcon className="w-5 h-5" strokeWidth={2.4} />
            Copy Invite Link
          </button>

          {/* SECONDARY: Share on WhatsApp */}
          <button
            onClick={shareWhatsApp}
            data-testid="remotely-invite-whatsapp"
            className="vc-cta vc-cta-secondary w-full rounded-full py-[15px] font-extrabold flex items-center justify-center gap-3"
            style={{ fontSize: "15.5px" }}
          >
            <WhatsAppIcon size={22} />
            Share on WhatsApp
          </button>
        </div>

        {/* Tappable "I have a room code" chip */}
        <button
          onClick={onJoinInstead}
          data-testid="remotely-invite-have-code"
          className="mx-auto flex items-center justify-center gap-2 rounded-full px-4 py-2 text-white/90 hover:text-white text-[12.5px] font-extrabold tracking-wide transition active:scale-[0.97]"
          style={{
            background: "rgba(20, 14, 26, 0.55)",
            border: "1.5px solid rgba(255, 46, 154, 0.55)",
            boxShadow: "0 0 10px rgba(255, 46, 154, 0.28)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Gamepad2 className="w-[16px] h-[16px]" style={{ color: "#FF2E9A", filter: "drop-shadow(0 0 4px #FF2E9A)" }} strokeWidth={2.2} />
          I have a room code
          <ArrowRight className="w-[14px] h-[14px] text-white/70" strokeWidth={2.4} />
        </button>

        <div className="flex-1 min-h-[28px]" />

        {/* Footer privacy line */}
        <div className="flex items-center justify-center gap-1.5 text-white/55 text-[11.5px] font-medium mt-4">
          <Lock className="w-3.5 h-3.5" />
          Private. Secure. Just between you two.
        </div>
      </main>
    </div>
  );
}
